"use client";
/** The page-level pill row the calculators use (CalcNav), for any set
 *  of sibling routes: a segmented control with a sliding thumb (the faint
 *  brand tint of a picked segment, SEGMENT_ON; owner, 2026-10-31: the
 *  picked side should stand out, not only carry a dark outline) and an
 *  icon per tab; a 2-column grid on phones so every tab stays visible, one
 *  row from lg. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { usePathname } from "next/navigation";
import { useT } from "@/lib/locale-context";
import type { NavGroupItem } from "@/lib/nav-groups";
import { useAnalysisBusy } from "@/lib/analysis-busy";
import { BarChart3 } from "lucide-react";
import { SEGMENT_ON } from "@/lib/ui-classes";

export default function PageTabs({ tabs, icons, defaultHref, align = "center" }: {
  tabs: NavGroupItem[];
  icons: Record<string, typeof BarChart3>;
  /** The tab that matches when no other does (the bare route). */
  defaultHref: string;
  align?: "center" | "start";
}) {
  const t = useT();
  // A run in flight on the current page: the other tabs wait for it.
  const busy = useAnalysisBusy();
  const pathname = (usePathname() ?? "").replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
  // Whole segments only: "/analyze" must not match "/analyst".
  const activeIdx = (() => {
    for (let i = 0; i < tabs.length; i++) {
      const href = tabs[i].href;
      if (href !== defaultHref && (pathname === href || pathname.startsWith(href + "/"))) return i;
    }
    const d = tabs.findIndex((x) => x.href === defaultHref);
    return d < 0 ? 0 : d;
  })();
  // Sliding thumb: measured off the active tab (offset* is relative to
  // the positioned wrapper), re-measured on tab change and container
  // resize so the grid/flex breakpoint swap can't strand it.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [thumb, setThumb] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const measure = useCallback(() => {
    const el = tabRefs.current[activeIdx];
    if (!el) return;
    setThumb({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight });
  }, [activeIdx]);
  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure]);
  const cols = tabs.length <= 2 ? "grid-cols-2 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3";
  return (
    <div className={`mb-10 flex ${align === "center" ? "justify-center" : "justify-start"}`}>
      <div
        ref={wrapRef}
        className={`relative grid w-full max-w-md ${cols} gap-1 rounded-2xl border border-gray-200 bg-white p-1 sm:max-w-2xl lg:flex lg:w-auto lg:max-w-full lg:rounded-full`}
      >
        {thumb && (
          <span
            aria-hidden
            className={`absolute left-0 top-0 rounded-xl ${SEGMENT_ON} transition-[translate,width,height] duration-300 ease-[cubic-bezier(.22,1,.36,1)] lg:rounded-full`}
            style={{ translate: `${thumb.x}px ${thumb.y}px`, width: thumb.w, height: thumb.h }}
          />
        )}
        {tabs.map((tab, i) => {
          const Icon = icons[tab.href] ?? BarChart3;
          const active = i === activeIdx;
          return (
            <Link
              key={tab.href}
              href={tab.href}
              ref={(el) => { tabRefs.current[i] = el; }}
              aria-current={active ? "page" : undefined}
              aria-disabled={busy && !active ? true : undefined}
              tabIndex={busy && !active ? -1 : undefined}
              onClick={(e) => { if (busy && !active) e.preventDefault(); }}
              className={`no-scale relative z-10 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2 text-center text-[13px] font-semibold transition-colors lg:rounded-full lg:px-4 sm:text-sm ${
                active ? "text-indigo-700" : busy ? "cursor-not-allowed text-gray-300" : "text-gray-500 hover:text-gray-800"
              } ${active && !thumb ? SEGMENT_ON : ""}`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(tab.labelKey)}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
